import { FirebaseApp } from './FirebaseApp.js';

export class ModelOptions {
  static async add(item = {}, options = {}) {
    await ModelOptions.modify({
      actions: [{ action: 'add', item }],
      ...options
    });
  }

  static async remove(item = {}, options = {}) {
    await ModelOptions.modify({
      actions: [{ action: 'remove', item }],
      ...options
    });
  }

  static async update(item = {}, options = {}) {
    await ModelOptions.modify({
      actions: [{ action: 'update', item }],
      ...options
    });
  }

  /**
   * A private static class to apply an array of actions to a model options array.
   * Note: Does not modify the original array.
   * @param {Array} actions
   * @param {Array} modelOptions
   * @returns {Array} - The new model options array.
   */
  static #applyActions(actions, modelOptions = []) {
    if (!Array.isArray(actions)) throw new Error('`actions` must be an array');
    if (!Array.isArray(modelOptions)) throw new Error('`modelOptions` must be an array');
    // Disconnect the `modelOptions` array from the original array.
    modelOptions = [...modelOptions];
    // Loop actions and apply each to the `modelOptions` array.
    actions.forEach((action) => {
      // Skip this action if it doesn't have an item key.
      if (action.item?.key == null) return;
      if (action.action == 'update') {
        // Find the item in `modelOptions` and update it.
        const item = modelOptions.find((x) => x?.value == action.item.key);
        for (const key in action.item) item[key] = action.item[key];
      } else {
        // Start by removing any instances of the item from `modelOptions`.
        modelOptions = modelOptions.filter((x) => x?.value != action.item.key);
        // If the action is to add the item, then add it to the end of the array.
        if (action.action == 'add') modelOptions.push({ value: action.item.key, ...action.item });
      }
    });
    return modelOptions;
  }

  static async modify(options = {}) {
    // Make sure options.companyId is specified.
    if (!options.companyId) throw new Error('Missing companyId');
    // Make sure options.actions is an array.
    if (!Array.isArray(options.actions)) throw new Error('Missing actions array');

    // Check to see if model options exists.
    // Since the model options is an array, we must perform a transaction to update it.
    // But if model options doesn't exist yet, there is nothing to perform a transaction on, so we just set it.
    const modelOptionsRef = FirebaseApp(options.serverId).database().ref(`photoheight/company_space/${options.companyId}/model_options`);
    const modelOptionsExist = await modelOptionsRef.once('value').then((s) => s.exists());
    if (modelOptionsExist) {
      // Perform the transaction.
      await modelOptionsRef.transaction((modelOptions) => {
        if (Array.isArray(modelOptions)) {
          // Apply the actions to the existing  model options array.
          modelOptions = ModelOptions.#applyActions(options.actions, modelOptions);
        }
        return modelOptions;
      });
    } else {
      // Apply the actions to an empty array (which is default).
      const modelOptions = ModelOptions.#applyActions(options.actions);
      // Set the model options.
      await modelOptionsRef.set(modelOptions);
    }
  }
}
